import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Play, Rocket, XCircle } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RelativeTime } from "@/components/RelativeTime";
import { shortSha } from "@/lib/format";
import { cn } from "@/lib/utils";

interface ReleaseDep {
  id: string;
  branch: string;
  commitSha: string;
  commitMessage: string | null;
  createdAt: string;
  status: string;
}

interface Release {
  id: string;
  state: "active" | "completed" | "aborted";
  stages: Array<{ percent: number; durationSec: number | null }>;
  currentStage: number;
  percent: number | null;
  awaitingApproval: boolean;
  startedAt: string;
  advancedAt: string;
  finishedAt: string | null;
  abortReason: string | null;
  base: ReleaseDep | null;
  candidate: ReleaseDep | null;
  metrics: {
    canary: { requests: number; errors: number };
    stable: { requests: number; errors: number };
  };
}

/**
 * 007 (US1) — live rolling-release panel (rendered on the project
 * Overview). Shows the traffic split, per-variant error rates, stage
 * progress, and the approve/abort/complete controls.
 */
export function ReleasePanel() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const q = useQuery({
    enabled: Boolean(org && project),
    queryKey: ["release", org, project],
    queryFn: () => api.get<{ release: Release | null }>(`/orgs/${org}/projects/${project}/release`).then((r) => r.release),
    refetchInterval: (query) => (query.state.data?.state === "active" ? 5000 : 60_000),
  });

  const act = useMutation({
    mutationFn: (v: { action: "approve" | "abort" | "complete"; reason?: string }) =>
      api.post(`/orgs/${org}/projects/${project}/release/${q.data?.id}/action`, v),
    onSuccess: () => {
      // Worker applies the action asynchronously — poll a beat later.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["release", org, project] });
        qc.invalidateQueries({ queryKey: ["project-overview", org, project] });
      }, 1500);
    },
  });

  const r = q.data;
  if (!r) return null;
  // Only surface finished releases briefly-relevant context: show if active,
  // or finished within the last 30 minutes.
  const finishedRecently =
    r.finishedAt !== null && Date.now() - new Date(r.finishedAt).getTime() < 30 * 60_000;
  if (r.state !== "active" && !finishedRecently) return null;

  const errRate = (m: { requests: number; errors: number }) =>
    m.requests > 0 ? `${((m.errors / m.requests) * 100).toFixed(1)}%` : "—";

  return (
    <div
      className={cn(
        "of-card overflow-hidden border-l-4",
        r.state === "active"
          ? "border-l-accent"
          : r.state === "completed"
            ? "border-l-success"
            : "border-l-danger",
      )}
    >
      <div className="of-card-header flex-wrap gap-y-1">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Rocket className="h-3.5 w-3.5 text-muted-foreground" />
          {r.state === "active"
            ? `Rolling release — ${r.percent}% canary`
            : r.state === "completed"
              ? "Rolling release completed"
              : "Rolling release aborted"}
        </h3>
        <span className="text-2xs text-subtle-foreground">
          started <RelativeTime value={r.startedAt} />
        </span>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* stage progress */}
        <div className="flex flex-wrap items-center gap-1.5">
          {[...r.stages, { percent: 100, durationSec: null }].map((s, i) => {
            const reached = r.state === "completed" || i < r.currentStage || (r.state === "active" && i === r.currentStage);
            const current = r.state === "active" && i === r.currentStage;
            return (
              <div key={i} className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "text-2xs font-mono rounded px-1.5 py-0.5 border",
                    current
                      ? "bg-accent-subtle text-accent border-accent/40 font-semibold"
                      : reached
                        ? "bg-subtle text-foreground border-border"
                        : "bg-subtle/40 text-subtle-foreground border-border-muted",
                  )}
                >
                  {s.percent}%
                </span>
                {i < r.stages.length && <span className="text-subtle-foreground text-2xs">→</span>}
              </div>
            );
          })}
          {r.state === "active" && r.awaitingApproval && (
            <span className="text-2xs text-attention ml-2">waiting for approval</span>
          )}
        </div>

        {/* variants */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <VariantBox
            label="Stable"
            dep={r.base}
            metrics={r.metrics.stable}
            errRate={errRate(r.metrics.stable)}
            org={org!}
            project={project!}
          />
          <VariantBox
            label="Canary"
            dep={r.candidate}
            metrics={r.metrics.canary}
            errRate={errRate(r.metrics.canary)}
            org={org!}
            project={project!}
          />
        </div>

        {r.state === "aborted" && r.abortReason && (
          <div className="text-xs text-danger inline-flex items-center gap-1.5">
            <XCircle className="h-3.5 w-3.5" /> {r.abortReason}
          </div>
        )}
        {r.state === "completed" && (
          <div className="text-xs text-success inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Candidate is now the active production deployment.
          </div>
        )}

        {act.error instanceof ApiError && <div className="text-xs text-danger">{act.error.message}</div>}

        {r.state === "active" && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="primary" size="sm" disabled={act.isPending} onClick={() => act.mutate({ action: "approve" })}>
              {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Advance stage
            </Button>
            <Button variant="default" size="sm" disabled={act.isPending} onClick={() => act.mutate({ action: "complete" })}>
              Complete (100%)
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={act.isPending}
              onClick={() => {
                if (window.confirm("Abort this release? All traffic returns to the stable build and the candidate is torn down.")) {
                  act.mutate({ action: "abort", reason: "aborted from dashboard" });
                }
              }}
            >
              Abort
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function VariantBox({
  label,
  dep,
  metrics,
  errRate,
  org,
  project,
}: {
  label: string;
  dep: ReleaseDep | null;
  metrics: { requests: number; errors: number };
  errRate: string;
  org: string;
  project: string;
}) {
  return (
    <div className="border border-border-muted rounded-md px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-2xs uppercase tracking-wider text-subtle-foreground">{label}</span>
        <span className={cn("font-mono text-2xs", metrics.errors > 0 ? "text-danger" : "text-subtle-foreground")}>
          {metrics.requests} req · {errRate} 5xx
        </span>
      </div>
      {dep ? (
        <Link
          to={`/orgs/${org}/projects/${project}/deployments/${dep.id}`}
          className="font-mono text-xs hover:underline truncate block mt-1"
        >
          {shortSha(dep.commitSha)} {dep.commitMessage ? `— ${dep.commitMessage}` : ""}
        </Link>
      ) : (
        <div className="text-xs text-subtle-foreground mt-1">—</div>
      )}
    </div>
  );
}
